import ApplicationConf from 'conf/ApplicationConf'
import { push } from '@lagunovsky/redux-react-router'
import fetch from 'isomorphic-fetch'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import LogAction from '../../../../log/actions/LogAction'
import {
    checkAuth,
    checkAuthWithoutKicking,
    checkError,
    getAuthorization,
    getJson,
} from '../../../../utils/ActionUtils'
import { hasValue } from '../../../../utils/NumberUtil'
import {
    RECEIVE_ALL_ANTENNAS,
    RECEIVE_ANTENNA,
    RECEIVE_ALL_ANTENNA_SITUATIONS,
    RECEIVE_ANTENNAS_LAST_SITUATIONS,
    RECEIVE_ANTENNA_TYPES,
    RESET_ANTENNA,
} from '../constants/AntennaConstants'

const AntennaAction = {
    receiveAntennas(antennas) {
        return { type: RECEIVE_ALL_ANTENNAS, antennas }
    },
    promiseAntennas() {
        return fetch(ApplicationConf.materiel.antennas(), {
            method: 'GET',
            headers: getAuthorization(),
        })
            .then(checkAuth)
            .then(getJson)
            .then(checkError)
    },
    fetchAntennas() {
        return (dispatch) => {
            return AntennaAction.promiseAntennas()
                .then((json = []) => {
                    dispatch(AntennaAction.receiveAntennas(json))
                    if (json.length) {
                        dispatch(AntennaAction.fetchAntennasLastSituations())
                    }
                })
        }
    },

    receiveAntenna(antenna) {
        return { type: RECEIVE_ANTENNA, antenna }
    },
    promiseAntenna(id) {
        return fetch(ApplicationConf.materiel.antenna(id), {
            headers: getAuthorization(),
        })
            .then(checkAuthWithoutKicking)
            .then(getJson)
            .then(checkError)
    },
    fetchAntenna: id => dispatch => {
        return AntennaAction.promiseAntenna(id)
            .then((json = []) => {
                dispatch(AntennaAction.receiveAntenna(json))
                return json
            })
            .catch(e => {
                dispatch(LogAction.logError(`${i18n.fetchError + i18n.antenna} : ${e}`))
                dispatch(ToastrAction.error(i18n.fetchError + i18n.antenna))
            })
    },

    updateAntenna(antenna) {
        return (dispatch) => {
            return fetch(ApplicationConf.materiel.antenna(antenna.id), {
                method: 'PUT',
                headers: getAuthorization(),
                body: JSON.stringify(antenna),
            })
                .then(checkAuth)
                .then(getJson)
                .then(checkError)
                .then(json => {
                    if (json.update === 1) {
                        dispatch(ToastrAction.success(i18n.elementUpdateSuccess))
                        dispatch(AntennaAction.fetchAntenna(antenna.id))
                        dispatch(AntennaAction.fetchAntennas())
                        return true
                    }
                    throw new Error(json)
                })
                .catch(err => {
                    const label = i18n.updateError + (err.message === '409 Conflict' ? i18n.immoNumberOrSerialNumberAlreadyUse : i18n.antenna)
                    dispatch(LogAction.logError(`${label} ${err}`))
                    dispatch(ToastrAction.error(label))
                })
        }
    },

    createAntenna(antenna) {
        return (dispatch) => {
            return fetch(ApplicationConf.materiel.antennas(), {
                method: 'POST',
                headers: getAuthorization(),
                body: JSON.stringify(antenna),
            })
                .then(checkAuth)
                .then(getJson)
                .then(checkError)
                .then(json => {
                    if (json.insert === 1 && hasValue(json.id)) {
                        dispatch(AntennaAction.fetchAntennas())
                        dispatch(push(`/materiel/antenna/${json.id}`))
                        dispatch(ToastrAction.success(i18n.elementCreateSuccess))
                        return json.id
                    }
                    throw new Error(json)
                })
                .catch(err => {
                    const label = i18n.updateError + (err.message === '409 Conflict' ? i18n.immoNumberOrSerialNumberAlreadyUse : i18n.antenna)
                    dispatch(LogAction.logError(`${label} ${err}`))
                    dispatch(ToastrAction.error(label))
                })
        }
    },

    deleteAntenna(id) {
        return (dispatch) => {
            return fetch(ApplicationConf.materiel.antenna(id), {
                method: 'DELETE',
                headers: getAuthorization(),
            })
                .then(checkAuth)
                .then(getJson)
                .then(checkError)
                .then(() => {
                    dispatch(AntennaAction.fetchAntennas())
                    dispatch(push('/materiel/antenna'))
                    dispatch(ToastrAction.success(i18n.elementDeleteSuccess))
                })
                .catch(err => {
                    dispatch(LogAction.logError(`${i18n.deleteError + i18n.antenna} : ${err}`))
                    dispatch(ToastrAction.error(i18n.deleteError + i18n.antenna))
                })
        }
    },

    receiveAntennaSituations(antennaSituations) {
        return { type: RECEIVE_ALL_ANTENNA_SITUATIONS, antennaSituations }
    },
    fetchAntennaSituations: id => dispatch => {
        return fetch(ApplicationConf.materiel.antennaSituations(id), {
            method: 'GET',
            headers: getAuthorization(),
        })
            .then(checkAuth)
            .then(getJson)
            .then(checkError)
            .then(json => {
                dispatch(AntennaAction.receiveAntennaSituations(json))
            })
    },

    receiveAntennasLastSituations(antennasLastSituations) {
        return { type: RECEIVE_ANTENNAS_LAST_SITUATIONS, antennasLastSituations }
    },
    fetchAntennasLastSituations: () => dispatch => {
        return fetch(ApplicationConf.materiel.antennasLastSituation(), {
            method: 'GET',
            headers: getAuthorization(),
        })
            .then(checkAuth)
            .then(getJson)
            .then(checkError)
            .then((json = []) => {
                dispatch(AntennaAction.receiveAntennasLastSituations(json))
            })
    },

    receiveAntennaTypes(antennaTypes) {
        return { type: RECEIVE_ANTENNA_TYPES, antennaTypes }
    },
    promiseAntennaTypes() {
        return fetch(ApplicationConf.materiel.antennaTypes(), {
            method: 'GET',
            headers: getAuthorization(),
        })
            .then(checkAuth)
            .then(getJson)
            .then(checkError)
    },
    fetchAntennaTypes() {
        return (dispatch) => {
            return AntennaAction.promiseAntennaTypes()
                .then((json = []) => {
                    dispatch(AntennaAction.receiveAntennaTypes(json))
                })
        }
    },
    addAntennaTypes(antennaType, callback = () => { }) {
        return (dispatch) => {
            return fetch(ApplicationConf.materiel.antennaTypes(), {
                method: 'POST',
                headers: getAuthorization(),
                body: JSON.stringify(antennaType),
            })
                .then(checkAuth)
                .then(getJson)
                .then(checkError)
                .then(json => {
                    if (json.insert >= 1 && json.id) {
                        dispatch(AntennaAction.fetchAntennaTypes())
                        dispatch(ToastrAction.success(i18n.addSuccess))
                        callback({ ...antennaType, materielType: json.id })
                    } else {
                        throw new Error()
                    }
                })
                .catch(err => {
                    dispatch(LogAction.logError(`${i18n.createError + i18n.antenna} : ${err}`))
                    dispatch(ToastrAction.error(i18n.createError + i18n.antenna))
                })
        }
    },
    updateAntennaType(antennaType) {
        return (dispatch) => {
            return fetch(ApplicationConf.materiel.antennaTypes(), {
                method: 'PUT',
                headers: getAuthorization(),
                body: JSON.stringify(antennaType),
            })
                .then(checkAuth)
                .then(getJson)
                .then(checkError)
                .then(() => {
                    dispatch(AntennaAction.fetchAntennaTypes())
                    dispatch(ToastrAction.success(i18n.updateSuccess))
                })
                .catch(err => {
                    dispatch(LogAction.logError(`${i18n.updateError + i18n.antenna} : ${err}`))
                    dispatch(ToastrAction.error(i18n.updateError + i18n.antenna))
                })
        }
    },
    deleteAntennaType(id, cb = () => {}) {
        return (dispatch) => {
            return fetch(ApplicationConf.materiel.antennaType(id), {
                method: 'DELETE',
                headers: getAuthorization(),
            })
                .then(checkAuth)
                .then(getJson)
                .then(checkError)
                .then(() => {
                    dispatch(AntennaAction.fetchAntennaTypes())
                    dispatch(ToastrAction.success(i18n.elementDeleteSuccess))
                    cb()
                })
                .catch(err => {
                    dispatch(LogAction.logError(`${i18n.deleteError + i18n.materielType} : ${err}`))
                    dispatch(ToastrAction.error(i18n.deleteError + i18n.materielType))
                })
        }
    },

    saveAntennaSituation(situation, shouldFetch = true) {
        return (dispatch) => {
            return fetch(ApplicationConf.materiel.addAntennaSituation(), {
                method: 'POST',
                headers: getAuthorization(),
                body: JSON.stringify(situation),
            })
                .then(checkAuth)
                .then(getJson)
                .then(checkError)
                .then(() => {
                    dispatch(AntennaAction.fetchAntennasLastSituations())
                    dispatch(ToastrAction.success(i18n.situationAddSuccess))
                    if (shouldFetch) {
                        dispatch(AntennaAction.fetchAntennaSituations(situation.idAntenne))
                    }
                })
        }
    },
    updateAntennaSituation(situation) {
        return (dispatch) => {
            return fetch(ApplicationConf.materiel.saveAntennaSituation(situation.id), {
                method: 'PUT',
                headers: getAuthorization(),
                body: JSON.stringify(situation),
            })
                .then(checkAuth)
                .then(getJson)
                .then(checkError)
                .then(() => {
                    dispatch(AntennaAction.fetchAntennasLastSituations())
                    dispatch(ToastrAction.success(i18n.saveSituationSucess))
                    dispatch(AntennaAction.fetchAntennaSituations(situation.idAntenne))
                })
        }
    },
    deleteAntennaSituation(id, idAntenne) {
        return (dispatch) => {
            return fetch(ApplicationConf.materiel.deleteAntennaSituation(id), {
                method: 'DELETE',
                headers: getAuthorization(),
            })
                .then(checkAuth)
                .then(getJson)
                .then(checkError)
                .then(() => {
                    dispatch(AntennaAction.fetchAntennasLastSituations())
                    dispatch(ToastrAction.success(i18n.situationDeleteSuccess))
                    dispatch(AntennaAction.fetchAntennaSituations(idAntenne))
                })
        }
    },

    resetAntenna() {
        return { type: RESET_ANTENNA }
    },
}

export default AntennaAction